import { useSetAtom } from "jotai";
import { useCallback } from "react";
import { useIsRejoinEnabled as useBaseIsRejoinEnabled } from "../session-grid/reconnect/useIsRejoinEnabled";
import { useDisconnectRoomCallback as useBaseDisconnectRoomCallback } from "../useDisconnectRoomCallback";
import { isRejoinPromptOpenAtom } from "../../video-sdk/core/atoms";
import { TrackingEvents } from "../../events";
import { RoomEventHandlerMap } from "../../video-sdk/core/room/types";
import { roomToDiagnosticInfo } from "../../video-sdk/core/utils";
import { RoomDelegator } from "../../video-sdk/core/room/RoomDelegator";
import { useAppDependenciesContext } from "../../state";

export type UseDisconnectedEventHandlerCallbackOptions = {
  useIsRejoinEnabled?: typeof useBaseIsRejoinEnabled;
  useDisconnectRoomCallback?: typeof useBaseDisconnectRoomCallback;
};

/**
 * Handles the room disconnected event
 */
export function useDisconnectedEventHandlerCallback({
  useIsRejoinEnabled = useBaseIsRejoinEnabled,
  useDisconnectRoomCallback = useBaseDisconnectRoomCallback,
}: UseDisconnectedEventHandlerCallbackOptions = {}) {
  const teardownRoom = useDisconnectRoomCallback();
  const [_, checkRejoinIsEnabled] = useIsRejoinEnabled();
  const setIsRejoinPromptOpen = useSetAtom(isRejoinPromptOpenAtom);
  const { sendLoggingEvents } = useAppDependenciesContext();

  return useCallback(
    async (
      room: RoomDelegator,
      error: Error | undefined,
      {
        isRoomRejoinable,
        onTeardownRoomCompleted,
      }: {
        isRoomRejoinable?: boolean;
        /**
         * Custom side effect dispatched after the room is torn down. This
         * callback will not be executed if the rejoin flow is triggered.
         */
        onTeardownRoomCompleted?: RoomEventHandlerMap["disconnected"];
      } = {},
    ) => {
      const logParams = error
        ? { error, isRoomRejoinable, logLevel: "error" as const }
        : { logLevel: "info" as const };

      const canRejoin = checkRejoinIsEnabled() && isRoomRejoinable;
      sendLoggingEvents(
        TrackingEvents.ROOM_DISCONNECTED,
        {
          room: roomToDiagnosticInfo(room),
          canRejoin,
        },
        { ...logParams, message: "Disconnected from room" },
      );

      // start rejoin flow
      if (canRejoin) {
        setIsRejoinPromptOpen(true);
      }

      // cleanup app state
      await teardownRoom({
        connectionError: error,
        skipLeaveRoomAction: canRejoin,
      });

      onTeardownRoomCompleted?.(room, error, { isRoomRejoinable: canRejoin });
    },
    [
      sendLoggingEvents,
      checkRejoinIsEnabled,
      teardownRoom,
      setIsRejoinPromptOpen,
    ],
  );
}
